import React from "react";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/site/PageHeader";
import { SiteFooter, FinalCTA } from "@/components/site/FinalCTA";
import Memorials from "@/components/site/Memorials";
import { PAGES } from "@/constants/testIds";

const TRADITIONS = [
  {
    faith: "Hindu",
    body: "For many Hindu families, cremation is the customary practice, symbolising the return of the body to the five elements of nature. Funeral rites are generally performed by close family members and are often followed by prayers, gatherings and remembrance ceremonies. Many families observe rituals on specific days after the funeral and continue to remember loved ones during annual memorial observances. Acts of charity, feeding people in need, and offering prayers are common expressions of remembrance. Memories are also preserved through family stories, photographs, heirlooms and visits to places of significance.",
  },
  {
    faith: "Sikh",
    body: "Sikh funeral ceremonies emphasise acceptance of the natural cycle of life and trust in the will of the Divine. Cremation is common, followed by prayers, hymns from the Guru Granth Sahib, and gatherings where family and community come together in support. Rather than focusing solely on loss, Sikh traditions often encourage gratitude for the person's life and contributions while remembering them through prayer, service and acts of kindness.",
  },
  {
    faith: "Buddhist",
    body: "Buddhist funeral practices vary among traditions and regions. Ceremonies often include chanting, meditation and reflections on compassion, impermanence and the continuity of life. Memorial gatherings may be held on significant days after death, and families frequently honour loved ones through charitable activities and acts that benefit others.",
  },
  {
    faith: "Jain",
    body: "Jain communities generally emphasise simplicity, non-violence and spiritual reflection. Funeral ceremonies often focus on prayers, meditation and remembrance of the individual's virtues and ethical life. Families may honour the departed by supporting educational, charitable or community initiatives that reflect the person's values.",
  },
  {
    faith: "Muslim",
    body: "For Muslims, burial is the traditional practice. Funeral prayers are offered by the community, and burial usually takes place as soon as practical after death. Families gather to offer prayers, comfort one another and remember the deceased with respect and humility. Many families continue remembrance through prayers, charitable giving and caring for relatives and neighbours in the spirit of the departed.",
  },
  {
    faith: "Christian",
    body: "Christian funeral services commonly include prayers, scripture readings, hymns and words of remembrance. Depending on family preference and denomination, burial or cremation may follow. Families often visit gravesites, celebrate memorial services and gather on anniversaries to reflect on the life and legacy of those they love.",
  },
  {
    faith: "Parsi (Zoroastrian)",
    body: "Parsi communities follow distinctive funeral customs rooted in respect for nature and the elements. Their ceremonies emphasise dignity, remembrance and prayer while supporting grieving families through close community bonds.",
  },
  {
    faith: "Indigenous & Tribal",
    body: "India's tribal and indigenous communities maintain a remarkable diversity of funeral traditions, reflecting local beliefs, ancestral customs and deep connections to the land. Some practise burial, others cremation, and many include music, dance, storytelling or communal gatherings that celebrate the person's role within the community. These traditions highlight the importance of preserving local cultural heritage alongside individual family histories.",
  },
];

const REGIONS = [
  { name: "Northern India", body: "Families may gather for prayer meetings, community meals and annual remembrance ceremonies. Rivers, pilgrimage sites and ancestral homes often hold special significance." },
  { name: "Southern India", body: "Remembrance frequently includes family gatherings, religious observances, temple visits, charitable offerings and the careful preservation of family records and photographs." },
  { name: "Eastern India", body: "Communities often combine religious rituals with storytelling, shared meals and collective remembrance — emphasising continuity between generations." },
  { name: "Western India", body: "Many families commemorate loved ones through prayer, charitable donations, cultural traditions and annual family reunions." },
  { name: "Northeastern India", body: "Funeral customs vary widely among different ethnic groups and faiths, reflecting a rich tapestry of indigenous traditions alongside Hindu, Christian, Buddhist and other practices." },
];

const MEMENTOES = [
  "Photographs and albums",
  "Handwritten letters",
  "Family recipes",
  "Recorded interviews",
  "Audio memories",
  "Videos",
  "Awards and certificates",
  "Military service records",
  "Family businesses",
  "Personal journals",
  "Artwork",
  "Favourite books",
  "Music collections",
  "Community service projects",
  "Trees planted in memory",
  "Scholarships and charitable initiatives",
];

const MEMORIAL_INCLUDES = [
  "A biography celebrating the person's life",
  "Family relationships and genealogy",
  "Photographs across different life stages",
  "Videos and audio recordings",
  "Personal stories shared by relatives and friends",
  "Important milestones and achievements",
  "Community contributions and tributes",
  "Favourite sayings, poems or songs",
  "Places that were meaningful to them",
  "Memorial dates and family anniversaries",
  "Acts of service, charitable work and lasting contributions",
];

const STORY_LOSSES = [
  "Children may never hear how their grandparents met.",
  "Great-grandchildren may never know why a family moved from one village to another.",
  "Photographs lose context.",
  "Names become forgotten.",
  "Family traditions disappear.",
];

export default function CelebrateALifePage() {
  return (
    <div data-testid={PAGES.celebrate} className="min-h-screen bg-ivory text-ink">
      <PageHeader
        eyebrow="Celebrate a Life · Indian"
        title="Honouring every"
        italic="journey"
        tail="— preserving every memory."
      >
        <div className="mt-8 max-w-3xl">
          <p className="font-serif italic text-tulsi text-xl md:text-2xl leading-relaxed">
            "Those we love never truly leave us. They live on in the stories we tell, the values
            they shared, the traditions they passed on, and the lives they touched."
          </p>
        </div>
      </PageHeader>

      {/* Opening essay */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <span className="text-xs uppercase tracking-[0.3em] text-saffron">A life remembered is a legacy preserved</span>
          <div className="mt-6 space-y-5 text-lg md:text-xl text-ink font-light leading-relaxed">
            <p className="first-letter:font-serif first-letter:text-6xl first-letter:mr-2 first-letter:float-left first-letter:leading-[0.85] first-letter:text-saffron">
              Every life tells a story. From the day we are born, we create memories with family,
              friends, neighbours and communities. We celebrate birthdays, weddings, milestones,
              festivals and achievements. Equally important is taking time to honour those who have
              completed their life's journey.
            </p>
            <p>
              Across India's many cultures, religions and traditions, remembering loved ones is
              considered both an act of love and a responsibility passed from one generation to the
              next. Whether through annual ceremonies, prayers, charitable acts or family
              gatherings, these moments keep memories alive and reinforce bonds that continue beyond
              a person's lifetime.
            </p>
            <p>
              Celebrate a Life on IndianFamilyTree.com provides a respectful place where families
              can preserve the stories, photographs, traditions and legacies of those who came
              before them — a lasting tribute that future generations can discover and cherish.
            </p>
          </div>
        </div>
      </section>

      {/* Existing memorial cards */}
      <Memorials />

      {/* Traditions of remembrance */}
      <section className="relative py-24 border-t border-ivory-300">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end">
            <div className="col-span-12 md:col-span-7">
              <span className="text-xs uppercase tracking-[0.3em] text-saffron">India's diverse traditions of remembrance</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
                One country,
                <span className="italic text-tulsi"> many ways </span>
                to say goodbye.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5">
              <p className="text-ink-50 text-base md:text-lg font-light leading-relaxed">
                Funeral and memorial customs vary across regions, communities and faiths — yet they
                share common values: honouring the deceased, supporting the grieving, and
                remembering a life with dignity. The notes below are broad overviews; individual
                families may follow their own regional or personal traditions.
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4 border-t border-ivory-300">
            {TRADITIONS.map((t, i) => (
              <motion.div
                key={t.faith}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.03 }}
                className="border-b border-ivory-300 py-8"
              >
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-[10px] tracking-[0.24em] text-saffron">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-ink">{t.faith}</h3>
                </div>
                <p className="mt-3 text-ink-50 text-base leading-relaxed font-light">{t.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Regional traditions */}
      <section className="relative py-24 bg-tulsi text-ivory">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5">
              <span className="text-xs uppercase tracking-[0.3em] text-marigold">Regional traditions across India</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight leading-[0.95]">
                Every corner of the country has its own way of remembering.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:pl-16">
              <ul>
                {REGIONS.map((r, i) => (
                  <li key={r.name} className="grid grid-cols-12 gap-6 border-b border-ivory/10 py-6">
                    <div className="col-span-12 md:col-span-4">
                      <div className="text-[10px] uppercase tracking-[0.24em] text-marigold">{String(i + 1).padStart(2, "0")}</div>
                      <div className="mt-1 font-serif text-2xl">{r.name}</div>
                    </div>
                    <div className="col-span-12 md:col-span-8 text-ivory/75 font-light leading-relaxed">
                      {r.body}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Memorials beyond rituals */}
      <section className="relative py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Memorials beyond rituals</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
              The most enduring memorials
              <span className="italic text-tulsi"> are the lives </span>
              we continue to influence.
            </h2>
            <p className="mt-6 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-md">
              Families preserve memories in dozens of ways — some ceremonial, some
              utterly ordinary. Every one of them helps future generations understand how their
              ancestors lived, loved, worked and contributed.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 md:pl-16">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {MEMENTOES.map((m, i) => (
                <li key={m} className="flex items-baseline gap-3 py-3 border-b border-ivory-200">
                  <span className="font-mono text-[10px] tracking-[0.24em] text-saffron">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif text-lg md:text-xl text-ink">{m}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Recording family stories */}
      <section className="relative py-24 bg-ivory-100/60 border-y border-ivory-300">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">The importance of recording family stories</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
              What we don't record,
              <span className="italic text-rust"> fades.</span>
            </h2>
            <p className="mt-6 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-md">
              As generations pass, invaluable memories can vanish if they are not recorded. By
              preserving these stories today, families create an enduring bridge between the past
              and the future.
            </p>
          </div>
          <div className="col-span-12 md:col-span-6 md:pl-10">
            <ul className="space-y-4">
              {STORY_LOSSES.map((s, i) => (
                <li key={i} className="border-l-2 border-rust/50 pl-4 py-2 text-ink font-serif italic text-xl leading-relaxed">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Create an eternal memory */}
      <section className="relative py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Create an eternal memory</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[0.95]">
              Every person leaves behind
              <span className="italic text-tulsi"> a legacy </span>
              that deserves to be remembered.
            </h2>
            <p className="mt-6 text-ink-50 text-base md:text-lg font-light leading-relaxed max-w-md">
              A digital memorial can become a place where family — living in the same hometown or
              across the world — comes together to celebrate a life. Over time it becomes more than
              a memorial; it becomes a living archive of family history.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 md:pl-16">
            <div className="border border-ivory-300 rounded-2xl p-8 bg-ivory">
              <div className="text-[10px] uppercase tracking-[0.28em] text-saffron">A memorial page can include</div>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
                {MEMORIAL_INCLUDES.map((m, i) => (
                  <li key={m} className="flex items-baseline gap-3 py-2 border-b border-ivory-200">
                    <span className="font-mono text-[10px] tracking-[0.24em] text-ink-50">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-ink font-light">{m}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Gift to future generations */}
      <section className="relative py-28 bg-ink text-ivory">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <span className="text-xs uppercase tracking-[0.3em] text-marigold">A gift for future generations</span>
          <h2 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
            The greatest inheritance is not
            <span className="italic text-marigold"> measured in wealth </span>
            alone.
          </h2>
          <p className="mt-8 text-ivory/70 text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
            It is found in the stories we preserve, the values we pass forward, the traditions we
            continue, and the memories we choose to protect. A child who reads about a
            great-grandparent's courage, kindness or resilience gains more than historical
            knowledge — they gain a sense of identity and belonging.
          </p>
          <p className="mt-6 font-serif italic text-2xl md:text-3xl text-marigold">
            Every family deserves that opportunity.
          </p>
        </div>
      </section>

      {/* Closing tagline */}
      <section className="relative py-24">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <p className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-ink leading-tight">
            Celebrate a Life. <span className="italic text-tulsi">Preserve a Legacy.</span> Inspire Future Generations.
          </p>
          <p className="mt-6 text-ink-50 text-base md:text-lg font-light max-w-2xl mx-auto leading-relaxed">
            Whether your loved one was a teacher, farmer, artist, entrepreneur, homemaker, soldier,
            doctor, craftsperson or community volunteer, their legacy is an irreplaceable part of
            your family's history.
          </p>
        </div>
      </section>

      <FinalCTA onStart={() => window.scrollTo({ top: 0, behavior: "smooth" })} />
      <SiteFooter />
    </div>
  );
}
